// Filtro tipado da aba Clientes (spec-012, [US-2] [US-3]): valida o objeto que a
// IA propôs e o aplica sobre a lista que a tela já carregou.
//
// ★ O modelo produz DADO, nunca consulta. A allow-list é barreira de segurança,
// não conveniência: sem ela, um passo de texto livre no meio do caminho viraria
// leitura arbitrária da base — bastaria `{campo:'cpf', operador:'like'}`.
//
// Quatro decisões, todas herdadas do módulo par no backend
// (`aawz_fee_fixo/core/filtro_clientes.py`, [T001]):
//
// 1. **Default deny.** Campo desconhecido é recusado. Campo novo no payload de
//    clientes chega aqui como desconhecido e continua recusado até alguém
//    classificá-lo — filtrar por dado novo vira decisão consciente.
// 2. **Tudo ou nada.** Um filtro inválido rejeita o objeto INTEIRO. Aplicar só
//    os válidos entregaria um recorte diferente do que o consultor leu no
//    preview, e o preview é contrato (§7 da spec).
// 3. **Recusa explica.** A mensagem nomeia o campo, o valor ofensor e o que era
//    esperado — inclusive o MOTIVO quando o campo é bloqueado de propósito.
// 4. **Não há caminho que pule a validação.** `aplicarFiltro` revalida e LANÇA;
//    a validação vale igual para o filtro que a IA propôs e para o que o
//    consultor editar depois.
//
// ⚠️ Esta lista é ESPELHO da do backend. O `.unit.js` compara as duas quando o
// repo do backend está no mesmo checkout: duas listas que deveriam ser iguais e
// ninguém compara divergem na primeira edição — e a que diverge para MAIS é um
// furo.

import { K, KLABEL } from '@modules/fee-fixo/constants/feeLab'

export const PREFIXO_CLASSE = 'alocacao_'

export const TIPO_NUMERO = 'numero'
export const TIPO_TEXTO = 'texto'
export const TIPO_BOOLEANO = 'booleano'

// Unidade de cada número: `roa >= 0.005` só é "0,5% ao ano" porque a unidade
// está declarada. É daqui que a tradução do filtro em linguagem natural ([T004])
// tira como escrever o valor na tela.
export const UNIDADE_REAIS = 'reais'
export const UNIDADE_FRACAO = 'fracao'
export const UNIDADE_FRACAO_AO_ANO = 'fracao_ao_ano'
export const UNIDADE_ANOS = 'anos'
export const UNIDADE_CONTAGEM = 'contagem'

// As quatro comparações de FAIXA. Um campo pode declarar este conjunto para
// ficar mais restrito que o default do tipo dele — a allow-list deixa estreitar,
// nunca alargar (`operadoresDoCampo`). Espelha `filtro_clientes.py:73`.
export const OPERADORES_DE_FAIXA = ['>=', '<=', '>', '<']

export const VALORES_STATUS_MIGRACAO = [
  'fee_fixo',
  'em_migracao',
  'nao_iniciado',
]
export const VALORES_STATUS_ELEGIBILIDADE = ['elegivel', 'nao_elegivel']
export const VALORES_STATUS_FINANCIAL_PLANNING = [
  'fez',
  'nao_fez',
  'indeterminado',
]

// --- Classes de ativo (spec-012 [US-5], achado A-36) ---
//
// Um campo por classe do Fee Lab, derivado de `K`/`KLABEL` — a mesma fonte que
// o laboratório usa; redigitá-las aqui criaria a enésima cópia da mesma lista.
//
// ★ O backend escreve as 7 à mão porque o NOSSO teste de paridade parseia o
// `.py` dele. Aqui ninguém parseia o `.js`, então derivar é o que impede a
// lista de envelhecer: classe nova no Fee Lab entra sozinha dos dois lados —
// lá pelo teste que cobra a declaração, aqui por construção.
//
// Só as comparações de FAIXA: a fração é medida, e igualdade exata sobre float
// ou não casa ninguém (erro silencioso) ou vira sonda do valor de cada cliente,
// que é enumeração da base disfarçada de filtro.
const CLASSES_COMO_CAMPOS = K.reduce((mapa, classe) => {
  mapa[`${PREFIXO_CLASSE}${classe}`] = {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_FRACAO,
    // "Equity ativo na carteira a partir de 30,00%" — o rótulo entra numa
    // FRASE no preview, e o `%` do valor já diz que é fração. O parêntese que
    // existia aqui repetia isso e quebrava a leitura no meio, sem desambiguar
    // nada que a unidade do valor não desambigue.
    rotulo: `${KLABEL[classe]} na carteira`,
    operadores: OPERADORES_DE_FAIXA,
    classe,
  }
  return mapa
}, {})

// Campos filtráveis: são CRITÉRIOS de priorização — dinheiro, estágio da
// jornada, geografia grossa —, nunca identificação de pessoa.
export const CAMPOS_PERMITIDOS = {
  // --- dinheiro ---
  auc: { tipo: TIPO_NUMERO, unidade: UNIDADE_REAIS, rotulo: 'PL' },
  patrimonio_total: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_REAIS,
    rotulo: 'patrimônio total',
  },
  pl_potencial: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_REAIS,
    rotulo: 'PL potencial',
  },
  pl_disponivel: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_REAIS,
    rotulo: 'PL disponível',
  },
  pl_fee_fixo: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_REAIS,
    rotulo: 'PL em fee fixo',
  },
  receita_atual: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_REAIS,
    rotulo: 'receita atual',
  },
  receita_fee_fixo: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_REAIS,
    rotulo: 'receita em fee fixo',
  },
  // Fração AO ANO: 0,005 é 0,5% a.a.
  roa: { tipo: TIPO_NUMERO, unidade: UNIDADE_FRACAO_AO_ANO, rotulo: 'ROA' },
  taxa_contratacao_fee_fixo: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_FRACAO_AO_ANO,
    rotulo: 'taxa contratada',
  },
  // ⚠️ O nome diz "percentual" e o valor é FRAÇÃO — o payload grava 1.0 para
  // 100% de ganho. "Ganho acima de 50%" escrito como `>= 50` não selecionaria
  // ninguém; é para isso que a unidade está declarada.
  ganho_percentual: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_FRACAO,
    rotulo: 'ganho na migração',
  },

  // --- estágio da jornada ---
  status_migracao: {
    tipo: TIPO_TEXTO,
    valores: VALORES_STATUS_MIGRACAO,
    rotulo: 'status da migração',
  },
  status_elegibilidade: {
    tipo: TIPO_TEXTO,
    valores: VALORES_STATUS_ELEGIBILIDADE,
    rotulo: 'elegibilidade',
  },
  status_financial_planning: {
    tipo: TIPO_TEXTO,
    valores: VALORES_STATUS_FINANCIAL_PLANNING,
    rotulo: 'planejamento financeiro',
  },
  fee_fixo_ativo: { tipo: TIPO_BOOLEANO, rotulo: 'já está em fee fixo' },
  roa_medido: { tipo: TIPO_BOOLEANO, rotulo: 'ROA medido' },
  pode_abrir_laboratorio: {
    tipo: TIPO_BOOLEANO,
    rotulo: 'pode abrir o laboratório',
  },

  // --- carteira e organização do escritório ---
  // Geografia grossa é o recorte do próprio §2 da spec ("ROA acima de 0,5% e
  // mora em SP"): cidade e estado não identificam ninguém sozinhos, e uma
  // allow-list que recusa o caso de uso da spec não serve para nada.
  estado: { tipo: TIPO_TEXTO, rotulo: 'estado' },
  cidade: { tipo: TIPO_TEXTO, rotulo: 'cidade' },
  segmentacao: { tipo: TIPO_TEXTO, rotulo: 'segmentação' },
  id_segmentacao: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_CONTAGEM,
    rotulo: 'id da segmentação',
  },
  id_profissional: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_CONTAGEM,
    rotulo: 'profissional responsável',
  },
  idade: { tipo: TIPO_NUMERO, unidade: UNIDADE_ANOS, rotulo: 'idade' },

  // --- composição da carteira ([US-5], achado A-36) ---
  // Entraram no espelho quando a listagem passou a publicá-las
  // (`core/composicao_carteira.py`). Antes disso não podiam: campo filtrável
  // que o payload não traz produz recorte de ZERO clientes com cara de resposta
  // legítima, e o consultor concluiria que não há ninguém naquele perfil.
  ...CLASSES_COMO_CAMPOS,

  // --- concentração derivada (spec-008-quem-migrar-clientes [T040/T041]) ---
  // Espelho de `filtro_clientes.py` do backend, ao lado de `alocacao_fl`:
  // `concentracao_fundos` (= alocacao_fa + alocacao_fp) e
  // `concentracao_credito_privado` (recorte DENTRO de `alocacao_fi`). Prefixo
  // `concentracao_` de propósito: `alocacao_` é reservado às 7 classes do
  // Fee Lab (`CAMPOS_DE_CLASSE`, derivado por prefixo logo abaixo). Mesma
  // régua de faixa das classes: fração medida, `==`/`!=`/`in` não casam
  // ninguém ou viram sonda do valor exato de cada cliente.
  concentracao_fundos: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_FRACAO,
    rotulo: 'concentração em fundos',
    operadores: OPERADORES_DE_FAIXA,
  },
  concentracao_credito_privado: {
    tipo: TIPO_NUMERO,
    unidade: UNIDADE_FRACAO,
    rotulo: 'concentração em crédito privado',
    operadores: OPERADORES_DE_FAIXA,
  },
}

// Campos que EXISTEM no payload e ficam de fora de propósito, com o motivo
// junto. Ficam separados do "desconhecido" porque a recusa precisa explicar: o
// modelo pediu um campo que ele vê no payload, e um "campo inválido" seco o faz
// tentar de novo com o mesmo campo.
//
// O critério: filtrar é RECORTAR UM GRUPO. Campo que serve para achar UMA
// pessoa, ou que descreve a pessoa em vez do negócio dela, não é critério de
// priorização — é busca ou é perfilamento.
export const CAMPOS_BLOQUEADOS = {
  nome_cliente:
    'identifica uma pessoa em vez de recortar um grupo; buscar pelo nome é a busca da tela',
  codigo_cliente:
    'identifica uma pessoa em vez de recortar um grupo; é a chave do cliente na corretora',
  id_cliente_app:
    'identifica uma pessoa em vez de recortar um grupo; enumerá-la é varrer a base cliente a cliente',
  data_nascimento:
    'é dado pessoal com granularidade de identificação; o recorte por faixa etária está em `idade`',
  sexo:
    'segmentar a abordagem comercial por sexo é discriminação, e a plataforma não oferece esse caminho',
  profissao:
    'é texto livre de dado pessoal: só teria uso com busca por trecho, que é o operador de varredura que esta lista recusa',
  data_cadastro:
    'data solta não é critério de priorização e o comparador de datas não existe nesta versão',
  data_registro:
    'data solta não é critério de priorização e o comparador de datas não existe nesta versão',
}

// Os campos de classe, DERIVADOS da allow-list por prefixo — nunca uma segunda
// lista. É a mesma técnica do backend (`filtro_clientes.py`, `CAMPOS_DE_CLASSE`):
// quem quiser saber "quais são os campos de classe" pergunta à allow-list.
//
// Até o A-36 estes campos viviam num mapa à parte, porque o backend não os tinha
// e a paridade precisava ser exata. Agora que a listagem publica a composição,
// manter dois conjuntos é que criaria a divergência.
export const CAMPOS_DE_CLASSE = Object.keys(CAMPOS_PERMITIDOS)
  .filter((campo) => campo.indexOf(PREFIXO_CLASSE) === 0)
  .reduce((mapa, campo) => {
    mapa[campo] = CAMPOS_PERMITIDOS[campo]
    return mapa
  }, {})

// O que a validação aceita. Continua exportado com este nome porque é o que a
// tela importa; hoje é exatamente o espelho, sem conjunto extra ao lado.
export const CAMPOS_FILTRAVEIS = { ...CAMPOS_PERMITIDOS }

export const ehCampoDeClasse = (campo) =>
  Object.prototype.hasOwnProperty.call(CAMPOS_DE_CLASSE, campo)

// Nenhum operador casa trecho de texto: `like`, `ilike`, `regex` e `contains`
// são o primitivo de varredura — com `%` devolvem a base inteira, e é assim que
// um filtro vira leitura arbitrária.
export const OPERADOR_LISTA = 'in'

export const OPERADORES_POR_TIPO = {
  [TIPO_NUMERO]: ['>=', '<=', '>', '<', '==', '!=', OPERADOR_LISTA],
  [TIPO_TEXTO]: ['==', '!=', OPERADOR_LISTA],
  [TIPO_BOOLEANO]: ['==', '!='],
}

export const OPERADORES_PERMITIDOS = Object.keys(OPERADORES_POR_TIPO)
  .reduce((todos, tipo) => todos.concat(OPERADORES_POR_TIPO[tipo]), [])
  .filter((operador, indice, lista) => lista.indexOf(operador) === indice)
  .sort()

// Schema estrito: chave a mais é carona, e carona que ninguém valida é a que
// vira consulta na primeira integração distraída.
export const CHAVES_DO_FILTRO = ['campo', 'operador', 'valor']

export const CHAVE_FILTROS = 'filtros'

export const MSG_OBJETO_INVALIDO =
  'O filtro precisa ser um objeto com a chave `filtros`, contendo uma lista de ' +
  'itens {campo, operador, valor}. Texto de consulta não é aceito.'

export const MSG_SEM_FILTROS =
  '`filtros` está vazio: nenhum filtro seleciona a carteira inteira, que não é ' +
  'um recorte. Informe ao menos um critério.'

// Erro do contrato — quem chamar `aplicarFiltro` com um objeto que não passou
// pela validação leva isto, e não um recorte silenciosamente errado.
export class FiltroInvalidoError extends Error {
  constructor(erros) {
    super(
      'Filtro inválido, nada foi aplicado: ' +
        (Array.isArray(erros) ? erros.join(' ') : String(erros))
    )
    this.name = 'FiltroInvalidoError'
    this.erros = Array.isArray(erros) ? erros : [String(erros)]
  }
}

// `bool` passa por número em JS (`true >= 1`): sem esta checagem,
// `fee_fixo_ativo = true` viraria comparação numérica silenciosa.
const ehNumero = (valor) =>
  typeof valor === 'number' && isFinite(valor) && typeof valor !== 'boolean'

const ehTextoUtil = (valor) => typeof valor === 'string' && valor.trim() !== ''

const tipoConfere = (valor, tipo) => {
  if (tipo === TIPO_NUMERO) return ehNumero(valor)
  if (tipo === TIPO_BOOLEANO) return typeof valor === 'boolean'
  return ehTextoUtil(valor)
}

const nomeDoTipo = (tipo) => {
  if (tipo === TIPO_NUMERO) return 'número'
  if (tipo === TIPO_BOOLEANO) return 'booleano (true/false)'
  return 'texto'
}

const comoTexto = (valor) => {
  if (typeof valor === 'string') return `"${valor}"`
  if (valor === undefined) return 'undefined'
  return JSON.stringify(valor)
}

const erroDoCampo = (posicao, campo) => {
  if (Object.prototype.hasOwnProperty.call(CAMPOS_BLOQUEADOS, campo)) {
    return (
      `Filtro ${posicao}: o campo \`${campo}\` não pode ser usado como filtro — ` +
      `${CAMPOS_BLOQUEADOS[campo]}.`
    )
  }
  return (
    `Filtro ${posicao}: campo ${comoTexto(campo)} não está na lista de campos ` +
    `filtráveis. Campos aceitos: ${Object.keys(CAMPOS_FILTRAVEIS).sort().join(', ')}.`
  )
}

const validarValor = ({ posicao, campo, meta, operador, valor }) => {
  const erros = []
  let itens = [valor]

  if (operador === OPERADOR_LISTA) {
    if (!Array.isArray(valor)) {
      return [
        `Filtro ${posicao}: o operador \`in\` do campo \`${campo}\` espera uma ` +
          `LISTA de valores; recebi ${comoTexto(valor)}.`,
      ]
    }
    if (!valor.length) {
      return [
        `Filtro ${posicao}: a lista de valores de \`${campo}\` está vazia, o que ` +
          'não seleciona ninguém. Informe ao menos um valor.',
      ]
    }
    itens = valor
  }

  itens.forEach((item) => {
    if (!tipoConfere(item, meta.tipo)) {
      erros.push(
        `Filtro ${posicao}: o campo \`${campo}\` espera ${nomeDoTipo(meta.tipo)}; ` +
          `recebi ${comoTexto(item)}.`
      )
      return
    }
    // Enum: `status_migracao == 'migrado'` — que não existe — passaria e
    // produziria um preview de 0 clientes com cara de resposta legítima.
    if (meta.valores && meta.valores.indexOf(item) === -1) {
      erros.push(
        `Filtro ${posicao}: ${comoTexto(item)} não é um valor de \`${campo}\`. ` +
          `Valores aceitos: ${meta.valores.join(', ')}.`
      )
    }
  })

  return erros
}

// Chave a mais é carona; chave a menos impede validar o resto do item.
const validarChaves = (posicao, filtro) => {
  const erros = []
  const faltando = CHAVES_DO_FILTRO.filter(
    (chave) => !Object.prototype.hasOwnProperty.call(filtro, chave)
  )
  const sobrando = Object.keys(filtro).filter(
    (chave) => CHAVES_DO_FILTRO.indexOf(chave) === -1
  )

  if (faltando.length) {
    erros.push(`Filtro ${posicao}: faltam as chaves ${faltando.join(', ')}.`)
  }
  if (sobrando.length) {
    erros.push(
      `Filtro ${posicao}: chaves não reconhecidas ${sobrando.join(', ')} — o ` +
        `filtro aceita apenas ${CHAVES_DO_FILTRO.join(', ')}.`
    )
  }
  return { erros, incompleto: faltando.length > 0 }
}

const campoEhPermitido = (campo) =>
  typeof campo === 'string' &&
  Object.prototype.hasOwnProperty.call(CAMPOS_FILTRAVEIS, campo)

const erroDoOperador = (posicao, operador) => {
  if (
    typeof operador === 'string' &&
    OPERADORES_PERMITIDOS.indexOf(operador) !== -1
  ) {
    return null
  }
  return (
    `Filtro ${posicao}: operador ${comoTexto(operador)} não é aceito. ` +
    `Operadores aceitos: ${OPERADORES_PERMITIDOS.join(', ')}. Busca por trecho ` +
    'de texto não existe aqui — ela varreria a base inteira.'
  )
}

/**
 * Os operadores que ESTE campo aceita.
 *
 * O default é o do tipo; um campo pode declarar `operadores` para ficar mais
 * restrito — é o caso da fração de carteira, que aceita só faixa. A
 * **interseção** é o que garante que a declaração só ESTREITA: um campo não
 * pode se conceder um operador que o tipo dele não tem. Substituir a lista do
 * tipo pela do campo abriria a porta para `{operadores: ['like']}` reintroduzir
 * a varredura que a allow-list inteira existe para impedir.
 *
 * Espelha `_operadores_do_campo` de `filtro_clientes.py`.
 *
 * @param {Object} meta Metadado do campo em `CAMPOS_FILTRAVEIS`.
 * @returns {Array<string>} Operadores aceitos, na ordem do tipo.
 */
export const operadoresDoCampo = (meta) => {
  const doTipo = OPERADORES_POR_TIPO[meta.tipo] || []
  if (!Array.isArray(meta.operadores)) return doTipo
  return doTipo.filter((operador) => meta.operadores.indexOf(operador) !== -1)
}

const erroDoOperadorNoTipo = (posicao, campo, meta, operador) => {
  const aceitos = operadoresDoCampo(meta)
  if (aceitos.indexOf(operador) !== -1) return null
  return (
    `Filtro ${posicao}: o operador \`${operador}\` não vale para o campo ` +
    `\`${campo}\`, que é ${nomeDoTipo(meta.tipo)}. Para ele, use: ` +
    `${aceitos.join(', ')}.`
  )
}

// Campo, operador e valor — nesta ordem, porque cada um só faz sentido depois
// do anterior: sem campo válido não há tipo, e sem tipo não há operador nem
// valor a conferir.
const validarCampoOperadorValor = (posicao, filtro) => {
  const { campo, operador, valor } = filtro
  const permitido = campoEhPermitido(campo)
  const erros = permitido ? [] : [erroDoCampo(posicao, campo)]

  const erroOperador = erroDoOperador(posicao, operador)
  if (erroOperador) return erros.concat(erroOperador)
  if (!permitido) return erros

  const meta = CAMPOS_FILTRAVEIS[campo]
  const erroNoTipo = erroDoOperadorNoTipo(posicao, campo, meta, operador)
  if (erroNoTipo) return erros.concat(erroNoTipo)

  return erros.concat(validarValor({ posicao, campo, meta, operador, valor }))
}

const validarItem = (posicao, filtro) => {
  if (!filtro || typeof filtro !== 'object' || Array.isArray(filtro)) {
    return [
      `Filtro ${posicao}: esperado um objeto {campo, operador, valor}; recebi ` +
        `${comoTexto(filtro)}.`,
    ]
  }

  const chaves = validarChaves(posicao, filtro)
  if (chaves.incompleto) return chaves.erros

  return chaves.erros.concat(validarCampoOperadorValor(posicao, filtro))
}

/**
 * Valida o objeto de filtro proposto pela IA — ou editado pelo consultor —
 * ANTES de qualquer uso.
 *
 * @param {Object} objeto `{ filtros: [{ campo, operador, valor }, ...] }`.
 * @returns {Object} `{ valido, filtros, erros }`. `filtros` volta intacto
 *   quando tudo passa e `[]` quando não — não existe aprovação parcial. `erros`
 *   traz TODOS os problemas de uma vez: reportar um por rodada faria o modelo
 *   corrigir no escuro.
 */
export const validarFiltro = (objeto) => {
  const listaBruta =
    objeto && typeof objeto === 'object' ? objeto[CHAVE_FILTROS] : null

  if (!Array.isArray(listaBruta)) {
    return { valido: false, filtros: [], erros: [MSG_OBJETO_INVALIDO] }
  }
  if (!listaBruta.length) {
    return { valido: false, filtros: [], erros: [MSG_SEM_FILTROS] }
  }

  const erros = listaBruta.reduce(
    (todos, filtro, indice) => todos.concat(validarItem(indice + 1, filtro)),
    []
  )

  if (erros.length) return { valido: false, filtros: [], erros }
  return { valido: true, filtros: listaBruta.slice(), erros: [] }
}

const COMPARADORES = {
  '>=': (valor, alvo) => valor >= alvo,
  '<=': (valor, alvo) => valor <= alvo,
  '>': (valor, alvo) => valor > alvo,
  '<': (valor, alvo) => valor < alvo,
  '==': (valor, alvo) => valor === alvo,
  '!=': (valor, alvo) => valor !== alvo,
  [OPERADOR_LISTA]: (valor, alvo) => alvo.indexOf(valor) !== -1,
}

// Ausência de dado NÃO satisfaz filtro nenhum — nem o negativo. Um cliente sem
// `roa` não é "ROA abaixo de 0,5%": é um cliente sobre quem não sabemos nada
// nesse campo, e afirmar o contrário colocaria gente errada na lista de ligação
// ([US-5] leva isso adiante para as classes de ativo).
const temDado = (valor) => valor !== undefined && valor !== null

const clienteAtende = (cliente, filtro) => {
  const valor = cliente ? cliente[filtro.campo] : undefined
  if (!temDado(valor)) return false
  return COMPARADORES[filtro.operador](valor, filtro.valor)
}

/**
 * Aplica os filtros sobre a lista que a tela já carregou. Client-side por
 * decisão de segurança: o modelo nunca alcança o banco.
 *
 * @param {Object} opts
 * @param {Array<Object>} opts.clientes Lista já carregada.
 * @param {Array<Object>} opts.filtros Filtros tipados.
 * @returns {Array<Object>} Os clientes que atendem a TODOS os filtros.
 * @throws {FiltroInvalidoError} Quando os filtros não passam na validação —
 *   é o que torna impossível aplicar sem validar.
 */
export const aplicarFiltro = ({ clientes, filtros } = {}) => {
  const validacao = validarFiltro({ [CHAVE_FILTROS]: filtros })
  if (!validacao.valido) throw new FiltroInvalidoError(validacao.erros)
  if (!Array.isArray(clientes)) return []

  return clientes.filter((cliente) =>
    validacao.filtros.every((filtro) => clienteAtende(cliente, filtro))
  )
}

const somarPl = (clientes) =>
  clientes.reduce(
    (soma, cliente) => soma + (ehNumero(cliente && cliente.auc) ? cliente.auc : 0),
    0
  )

const SEM_CLASSE_PEDIDA = {
  classesPedidas: [],
  clientesSemDadoDeClasse: 0,
  classeIndisponivel: false,
}

/**
 * Quanto da base tem, de fato, a composição de carteira que o filtro pediu.
 *
 * ★ [US-5] O ponto é distinguir DUAS respostas que a lista vazia confunde:
 * "nenhum cliente atende ao critério" e "ninguém tem esse dado ainda". A
 * segunda não é uma medição — tratá-la como 0% na classe colocaria (ou tiraria)
 * gente da lista de ligação por um dado que nunca foi importado.
 *
 * @param {Object} opts
 * @param {Array<Object>} opts.clientes Base carregada.
 * @param {Array<Object>} opts.filtros Filtros já validados.
 * @returns {Object} `{ classesPedidas, clientesSemDadoDeClasse,
 *   classeIndisponivel }` — `classeIndisponivel` só é `true` quando o filtro
 *   pede classe, a base tem clientes e NENHUM deles tem a composição.
 */
export const cobrituraDeClasse = ({ clientes, filtros } = {}) => {
  const campos = (Array.isArray(filtros) ? filtros : [])
    .map((filtro) => filtro && filtro.campo)
    .filter(ehCampoDeClasse)

  if (!campos.length) return { ...SEM_CLASSE_PEDIDA }

  const lista = Array.isArray(clientes) ? clientes : []
  const semDado = lista.filter((cliente) =>
    campos.some((campo) => !temDado(cliente && cliente[campo]))
  )

  return {
    classesPedidas: campos.map((campo) => CAMPOS_DE_CLASSE[campo].classe),
    clientesSemDadoDeClasse: semDado.length,
    // Base vazia não afirma indisponibilidade: não há sobre o que afirmar.
    classeIndisponivel: lista.length > 0 && semDado.length === lista.length,
  }
}

/**
 * Preview do recorte: o que o consultor lê ANTES de aplicar.
 *
 * ★ `selecionados` é a MESMA lista que `aplicarFiltro` devolve — o preview não
 * recalcula por outro caminho. Contagem que não bate com o resultado destrói a
 * confiança na sugestão inteira (§7), e o único jeito de garantir que batem é
 * não existirem dois caminhos.
 *
 * @param {Object} opts
 * @param {Array<Object>} opts.clientes Lista já carregada.
 * @param {Object} opts.objeto O que a tool devolveu.
 * @returns {Object} `{ valido, erros, filtros, selecionados, quantidade, pl }`.
 *   Inválido devolve tudo zerado — e nenhum cliente chega a ser lido.
 */
export const previewDoFiltro = ({ clientes, objeto } = {}) => {
  const validacao = validarFiltro(objeto)
  if (!validacao.valido) {
    return {
      valido: false,
      erros: validacao.erros,
      filtros: [],
      selecionados: [],
      quantidade: 0,
      pl: 0,
      ...SEM_CLASSE_PEDIDA,
    }
  }

  const selecionados = aplicarFiltro({ clientes, filtros: validacao.filtros })

  return {
    valido: true,
    erros: [],
    filtros: validacao.filtros,
    selecionados,
    quantidade: selecionados.length,
    pl: somarPl(selecionados),
    ...cobrituraDeClasse({ clientes, filtros: validacao.filtros }),
  }
}
